import { Badge, Button, Icon, RowCard } from "@elaniintc/goes-design-system";
import { NavLink, useParams } from "react-router";

import type { FormalityRequestType } from "@/types/entities/formality-request";
import type { FlowExecutionType } from "@/types/entities/flows";

type NodeExecution = FlowExecutionType["node_executions"][number];

export type Tramite = FormalityRequestType;

interface ResolutionNodeCardProps {
  procedureId: string;
  index: number;
  tramite?: Tramite;
  node?: NodeExecution;
  resolutionNodeExecution?: NodeExecution;
}

export const ResolutionNodeCard = ({
  procedureId,
  index,
  tramite,
  node,
  resolutionNodeExecution,
}: ResolutionNodeCardProps) => {
  const params = useParams<{
    id?: string;
    registryId?: string;
  }>();

  const activeNodeExecution = node || resolutionNodeExecution;

  const getTargetUrl = () => {
    if (params.id && params.registryId) {
      return `/expedientes/entidad/${params.id}/detalle-registro/${params.registryId}/tramite/${procedureId}/resolucion`;
    }
    return `/tramite/${procedureId}/resolucion`;
  };

  // Get status from resolution node execution inputs
  const getResolutionStatus = () => {
    // First try to get from resolution node execution
    if (activeNodeExecution?.inputs) {
      const statusInput = activeNodeExecution.inputs.find(
        (input) => input?.name === "status",
      ) as { name: string; value: string } | undefined;

      if (statusInput?.value) {
        return String(statusInput.value).toLowerCase();
      }
    }

    // Fallback to tramite.resolution.status or decision
    if (tramite?.resolution?.status) {
      return String(tramite.resolution.status).toLowerCase();
    }

    if (tramite?.resolution?.decision) {
      return String(tramite.resolution.decision).toLowerCase();
    }

    return "completed";
  };

  const status = getResolutionStatus();
  // Map status to badge configuration
  const getBadgeConfig = () => {
    switch (status) {
      case "approved":
      case "aprobado":
        return {
          variant: "success" as const,
          label: "Aprobado",
          icon: "check",
          color: "success" as const,
        };
      case "rejected":
      case "denegado":
      case "denied":
        return {
          variant: "danger" as const,
          label: "Denegado",
          icon: "xmark",
          color: "danger" as const,
        };
      case "canceled":
      case "cancelado":
      case "desistido":
        return {
          variant: "neutral" as const,
          label: "Cancelada",
          icon: "ban",
          color: "neutral" as const,
        };
      case "unfair":
        return {
          variant: "neutral" as const,
          label: "Improcedente",
          icon: "ban",
          color: "neutral" as const,
        };
      default:
        return {
          variant: "success" as const,
          label: "Completado",
          icon: "check",
          color: "success" as const,
        };
    }
  };

  const badgeConfig = getBadgeConfig();

  return (
    <NavLink to={getTargetUrl()}>
      <RowCard
        key={index}
        title={
          <div className="flex items-center gap-1">
            <p className="font-normal text-base text-body-loud truncate leading-tight">
              Resolución otorgada
            </p>
          </div>
        }
        color={badgeConfig.color}
        icon={{
          name: "file-circle-plus",
          type: "fas",
          size: "lg",
        }}
        subtitle="Visualiza la resolución otorgada y los documentos vinculados."
      >
        <RowCard.Right>
          <Badge
            variant={badgeConfig.variant}
            shape="square"
            className="h-6 text-sm"
          >
            {badgeConfig.label}
            <Icon
              type="fas"
              name={badgeConfig.icon}
              className={`text-${badgeConfig.color}-quiet text-xs`}
            />
          </Badge>
          <Button variant="ghost" size="xs" className="size-8">
            <Icon type="far" name="arrow-up-right" />
          </Button>
        </RowCard.Right>
      </RowCard>
    </NavLink>
  );
};
